import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import CloseButton from "../../buttons/CloseButton";
import { DefaultPrimaryButton } from "@/styles/globalstyles";
import type { ParsedExchangeRateImportResult } from "@/utils/excel/importPartExchangeRate";

interface ImportExchangeRatePartPreviewModalProps {
  open: boolean;
  data: ParsedExchangeRateImportResult | null;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

const formatOtherProcess = (items: { name: string; cost: number }[]): string =>
  items.length === 0 ? "-" : items.map((i) => `${i.name}: ${i.cost.toFixed(2)}`).join(", ");

const ImportExchangeRatePartPreviewModal = ({
  open,
  data,
  onClose,
  onConfirm,
  isSubmitting,
}: ImportExchangeRatePartPreviewModalProps) => {
  if (!data) return null;

  const { rows, errors, warnings } = data;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95vw", md: "85vw" },
          maxWidth: 1200,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 1, position: "relative" }}>
          <CloseButton onClick={onClose} />
          <Typography variant="h5" fontWeight={700}>
            Preview Import — Supplier {data.supplierCode}
          </Typography>

          {errors.length > 0 && (
            <Alert severity="error" sx={{ mt: 1.5, maxHeight: 200, overflow: "auto" }}>
              <Typography fontWeight={600} fontSize={13} mb={0.5}>
                พบข้อผิดพลาด {errors.length} รายการ — ต้องแก้ไขก่อนจึงจะ import ได้
              </Typography>
              {errors.map((e, i) => (
                <Typography key={i} fontSize={12}>
                  • {e.message}
                </Typography>
              ))}
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, maxHeight: 150, overflow: "auto" }}>
              {warnings.map((w, i) => (
                <Typography key={i} fontSize={12}>
                  • {w.message}
                </Typography>
              ))}
            </Alert>
          )}

          <Divider sx={{ mt: 1.5 }} />
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 1 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Part No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>A/C</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>R/T</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Material Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>OEM Other Process</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Spare Other Process</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.part_id}-${i}`}>
                    <TableCell>{r.part_number}</TableCell>
                    <TableCell>{r.ac || "-"}</TableCell>
                    <TableCell>{r.rt || "-"}</TableCell>
                    <TableCell>{r.currency_code}</TableCell>
                    <TableCell align="right">{r.price}</TableCell>
                    <TableCell>{formatOtherProcess(r.oem_other_process)}</TableCell>
                    <TableCell>{formatOtherProcess(r.spare_other_process)}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      ไม่มีข้อมูล
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {rows.length} Part
          </Typography>
          <Box display="flex" gap={2}>
            <Button variant="text" onClick={onClose} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              sx={DefaultPrimaryButton}
              onClick={onConfirm}
              disabled={isSubmitting || errors.length > 0}
            >
              {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImportExchangeRatePartPreviewModal;
